import React from "react";
import PropTypes from "prop-types";
import { AgreementTerms } from "./AgreementJourney";
import { formatAbn } from "utils/abn";
import { rctiTimestamp } from "utils/rctiTimestamp";
import { isSignedAgreement } from "utils/rctiAgreementSigned";
import { SavedSignature } from "components/DrawnSignature";
import { LuFileText } from "react-icons/lu";
import { agreementLabel, agreementReference } from "./RctiDesignSystem";
import "./agreementReview.css";

export default function AgreementDocument({
  agreement: a,
  preview = false,
  variant = "review",
}) {
  const supplierLayout = variant === "supplier";
  const reference = a._id ? agreementReference(a._id) : "Proposed agreement";
  const signed = isSignedAgreement(a);
  const proposed = ["proposed", "pending"].includes(a.status);
  const buyerPerson = a.buyerRepresentative?.name || a.buyer.name;
  const supplierPerson =
    (signed ? a.supplierSigner?.name : a.supplierRepresentative?.name) ||
    a.supplier.name;
  const supplierPosition = signed
    ? a.supplierSigner?.position
    : a.supplierRepresentative?.position;
  return (
    <article
      className={`rcti-agreement-document agreement-review-document agreement-contract-document${
        supplierLayout ? " agreement-supplier-document" : ""
      }`}
      aria-label={
        preview ? "Agreement document preview" : "RCTI agreement document"
      }
    >
      {!supplierLayout && (
        <header className="agreement-review-document-bar">
          <strong>
            <LuFileText aria-hidden="true" />
            Agreement document
          </strong>
          <span>
            v{a.version} · {preview ? "Preview" : "Agreement record"}
          </span>
        </header>
      )}
      <div className="agreement-review-paper">
        <header className="agreement-review-document-heading">
          <div className="supplier-document-brand-row">
            <p className="agreement-review-brand">{a.buyer.name}</p>
            {supplierLayout && (
              <span
                className={`rcti-agreement-badge rcti-agreement-badge--${a.status}`}
              >
                {agreementLabel(a.status)}
              </span>
            )}
          </div>
          <h2>Recipient created tax invoice agreement</h2>
          <p>
            {supplierLayout ? (
              <>
                {reference} · Wording v{a.version} · Prepared{" "}
                {rctiTimestamp(a.createdAt || a.buyerSignedAt)}
              </>
            ) : (
              <>
                {signed ? "Signed agreement" : "Standalone proposed contract"} ·{" "}
                {a._id ? `${reference} · ` : ""}Prepared{" "}
                {rctiTimestamp(a.createdAt || a.buyerSignedAt || a.preparedAt)}
              </>
            )}
          </p>
        </header>
        <div className="agreement-review-parties">
          {[
            ["Buyer / issuer", a.buyer, a.buyerRepresentative],
            [
              "Supplier / vendor",
              a.supplier,
              signed ? a.supplierSigner : a.supplierRepresentative,
            ],
          ].map(([label, party, person]) => (
            <section key={label}>
              <h3>{label}</h3>
              <strong>{party.name}</strong>
              <p>ABN {formatAbn(party.abnNumber) || "Not recorded"}</p>
              {supplierLayout && person?.name && (
                <p>
                  {person.name}
                  {person.position ? ` · ${person.position}` : ""}
                </p>
              )}
              {!preview && party.emailAddress && <p>{party.emailAddress}</p>}
              {supplierLayout && party.address && <p>{party.address}</p>}
            </section>
          ))}
        </div>
        {supplierLayout ? (
          <>
            <h3 className="supplier-contract-status">Status: {a.status}</h3>
            <dl className="supplier-contract-facts">
              <dt>Supply scope</dt>
              <dd>{a.scope}</dd>
              <dt>Effective date</dt>
              <dd>
                {signed
                  ? `${rctiTimestamp(
                      a.supplierSignedAt
                    )} · On supplier acceptance`
                  : "Upon supplier acceptance"}
              </dd>
              <dt>Agreed expiry</dt>
              <dd>No fixed expiry · Either party can suspend</dd>
              {proposed && (
                <>
                  <dt>Signing deadline</dt>
                  <dd>
                    Sign by ·{" "}
                    {a.invitationExpiresAt
                      ? rctiTimestamp(a.invitationExpiresAt)
                      : "7 days after creation"}
                  </dd>
                </>
              )}
            </dl>
          </>
        ) : (
          <div className="agreement-review-scope">
            {!preview && (
              <>
                <span
                  className={`rcti-agreement-badge rcti-agreement-badge--${a.status}`}
                >
                  {agreementLabel(a.status)}
                </span>
                <h3 className="agreement-contract-status">
                  Status: {a.status}
                </h3>
              </>
            )}
            <p>Covered supplies: {a.scope}</p>
            <p>
              Effective:{" "}
              {signed
                ? `${rctiTimestamp(
                    a.supplierSignedAt
                  )} · On supplier acceptance`
                : "upon supplier acceptance."}
            </p>
            <p>
              {preview
                ? "Agreed expiry: no fixed expiry."
                : "No fixed expiry · Either party can suspend."}{" "}
              Wording: v{a.version}.
            </p>
            {!preview && proposed && (
              <p>
                Sign by ·{" "}
                {a.invitationExpiresAt
                  ? rctiTimestamp(a.invitationExpiresAt)
                  : "7 days after creation"}
              </p>
            )}
          </div>
        )}
        <AgreementTerms terms={a.terms} />
        <div
          className={
            supplierLayout
              ? "supplier-signature-columns"
              : "agreement-signature-sections"
          }
        >
          <section className="agreement-review-acknowledgement">
            <h3>
              {supplierLayout
                ? "Buyer signature and consent"
                : "Buyer acknowledgement"}
            </h3>
            <p>
              {buyerPerson}
              {a.buyerRepresentative?.position
                ? `, ${a.buyerRepresentative.position}`
                : ""}
              , on behalf of {a.buyer.name}, acknowledges the buyer’s GST
              registration and obligation to notify the supplier if registration
              ceases, and confirms authority to propose and send this agreement.
            </p>
            <SavedSignature value={a.buyerSignature} label="Buyer signature" />
            <p className="agreement-review-muted">
              {a.buyerSignedAt
                ? `Buyer signed · ${rctiTimestamp(a.buyerSignedAt)}`
                : "Signature will be recorded when sent"}
              {a.buyer.emailAddress ? ` · ${a.buyer.emailAddress}` : ""}
            </p>
          </section>
          {signed ? (
            <section className="agreement-review-acknowledgement">
              <h3>Supplier acceptance</h3>
              <p>
                {supplierPerson}
                {supplierPosition ? `, ${supplierPosition}` : ""}, accepted this
                agreement on behalf of {a.supplier.name}. The saved signature,
                accepted wording version and acceptance time are recorded below.
              </p>
              <SavedSignature
                value={a.supplierSignature}
                label="Supplier signature"
              />
              <p className="agreement-review-muted">
                Supplier signed · {rctiTimestamp(a.supplierSignedAt)} · Wording
                v{a.version}
              </p>
            </section>
          ) : (
            <section className="agreement-review-pending">
              <h3>
                {proposed
                  ? "Supplier acceptance pending"
                  : "Supplier acceptance not recorded"}
              </h3>
              <p>
                {proposed
                  ? `${supplierPerson}${
                      supplierPosition ? `, ${supplierPosition}` : ""
                    }, is invited to review and sign for ${
                      a.supplier.name
                    }, confirm authority and acknowledge GST registration and the notification obligation. Acceptance name, drawn signature and date will be recorded at signing.`
                  : "No supplier acceptance is recorded for this agreement. A new eligible invitation is required before it can be used for RCTIs."}
              </p>
              <p>This proposal is not yet an active agreement.</p>
            </section>
          )}
        </div>
        {signed && a.status !== "active" && (
          <section className="agreement-review-pending">
            <h3>New issues and adjustments are blocked</h3>
            <p>
              Earlier issued documents and recorded payments remain in history.
              The saved agreement wording and signatures are retained.
            </p>
          </section>
        )}
        <p className="agreement-review-muted">
          This agreement is not a tax invoice and creates no invoice or payable.
          {preview
            ? " A reference and signing link are assigned only when you send it."
            : " Retain a copy with your business records."}
        </p>
      </div>
    </article>
  );
}
AgreementDocument.propTypes = {
  agreement: PropTypes.object.isRequired,
  preview: PropTypes.bool,
  variant: PropTypes.oneOf(["review", "supplier"]),
};
